"use client";

import React, { useState, useEffect, useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Share2, Linkedin, Facebook, Copy, Check, Smartphone, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
export default function Share() {
  const shouldReduceMotion = useReducedMotion();
  const [currentUrl, setCurrentUrl] = useState("https://elite-corpltd.com/");
  const [copied, setCopied] = useState(false);
  const [canNativeShare, setCanNativeShare] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [isZh, setIsZh] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsZh(localStorage.getItem("elite-language") === "zh-Hant");
      setCurrentUrl(window.location.href);
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        setCanNativeShare(true);
      }
      const handleLanguageChange = () => {
        setIsZh(localStorage.getItem("elite-language") === "zh-Hant");
      };
      window.addEventListener("storage", handleLanguageChange);
      window.addEventListener("elite-language-change", handleLanguageChange);
      return () => {
        window.removeEventListener("storage", handleLanguageChange);
        window.removeEventListener("elite-language-change", handleLanguageChange);
      };
    }
  }, []);
  const copyToClipboard = useCallback(async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(currentUrl);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = currentUrl;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        textArea.remove();
      }
      setCopied(true);
      setStatusMessage(isZh ? "已成功複製連結至剪貼簿。" : "Link copied to clipboard successfully.");
      setTimeout(() => {
        setCopied(false);
        setStatusMessage("");
      }, 3000);
    } catch {
      setStatusMessage(isZh ? "無法自動複製連結，請手動複製瀏覽器網址列。" : "Unable to copy link automatically. Please copy the browser address bar.");
    }
  }, [currentUrl, isZh]);
  const handleFacebookShare = () => {
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`;
    window.open(fbUrl, "_blank", "noopener,noreferrer,width=600,height=500");
  };
  const handleLinkedInShare = () => {
    const liUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`;
    window.open(liUrl, "_blank", "noopener,noreferrer,width=600,height=600");
  };
  const handleDeviceShare = async () => {
    if (canNativeShare) {
      try {
        await navigator.share({
          title: "Elite Group Corporation Limited",
          text: isZh ? "Elite Group Corporation Limited — 香港 OEM/ODM 玩具與消費品製造商。" : "Elite Group Corporation Limited — Hong Kong OEM/ODM toy and consumer goods manufacturer.",
          url: currentUrl
        });
        setStatusMessage(isZh ? "內容分享成功。" : "Content shared successfully.");
      } catch (err: unknown) {
        if (err instanceof Error && err.name !== "AbortError") {
          copyToClipboard();
        }
      }
    } else {
      copyToClipboard();
    }
  };
  const shareActions = [{
    id: "fb",
    label: isZh ? "在 Facebook 分享" : "Share on Facebook",
    icon: Facebook,
    onClick: handleFacebookShare,
    tooltip: isZh ? "在新視窗中開啟 Facebook 分享" : "Open Facebook share in new window",
    isExternal: true
  }, {
    id: "li",
    label: isZh ? "在 LinkedIn 分享" : "Share on LinkedIn",
    icon: Linkedin,
    onClick: handleLinkedInShare,
    tooltip: isZh ? "在新視窗中開啟 LinkedIn 分享" : "Open LinkedIn share in new window",
    isExternal: true
  }, {
    id: "device",
    label: canNativeShare ? isZh ? "透過裝置分享" : "Share via Device" : isZh ? "分享頁面" : "Share Page",
    icon: Smartphone,
    onClick: handleDeviceShare,
    tooltip: canNativeShare ? isZh ? "使用裝置原生分享面板" : "Use device native share sheet" : isZh ? "複製連結以分享" : "Copy link to share",
    isExternal: false
  }, {
    id: "copy",
    label: copied ? isZh ? "已複製連結" : "Link Copied" : isZh ? "複製連結" : "Copy Link",
    icon: copied ? Check : Copy,
    onClick: copyToClipboard,
    tooltip: isZh ? "複製當前頁面網址至剪貼簿" : "Copy current page URL to clipboard",
    isExternal: false,
    highlight: copied
  }];
  return <section id="sharethispage" data-nav="light" className="w-full bg-background py-12 md:py-16 text-foreground border-y border-border">
      <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <TooltipProvider delayDuration={200}>
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          ease: "easeOut"
        }} className="border border-border bg-card p-6 sm:p-8 shadow-xs">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              {}
              <div className="flex items-start gap-3.5 max-w-md">
                <div className="mt-1 flex size-9 shrink-0 items-center justify-center border border-border bg-secondary text-foreground">
                  <Share2 className="size-4 text-foreground" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block size-2 bg-primary" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {isZh ? "B2B 資源分享" : "B2B Resource Sharing"}
                    </span>
                  </div>
                  <h2 className="mt-1 text-lg sm:text-xl font-bold font-default text-foreground tracking-tight">
                    {isZh ? "分享此頁" : "Share This Page"}
                  </h2>
                  <p className="mt-1 text-xs sm:text-sm text-foreground/80 leading-relaxed font-default">
                    {isZh ? "將這些製造規格、實驗室認證或公司生產能力直接發送給您的採購團隊或合作夥伴。" : "Send these manufacturing specs, lab certifications, or company capabilities directly to your procurement team or partners."}
                  </p>
                </div>
              </div>

              {}
              <div className="flex flex-col sm:flex-row sm:flex-wrap lg:flex-nowrap items-stretch sm:items-center gap-2.5">
                {shareActions.map((action, i) => {
                const Icon = action.icon;
                return <div key={action.id} data-index={i} className="w-full sm:w-auto">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant={action.highlight ? "default" : "outline"} size="default" onClick={action.onClick} className={`w-full sm:w-auto justify-center font-default text-xs sm:text-sm font-medium tracking-normal transition-[background-color,border-color,color,box-shadow] duration-200 cursor-pointer ${action.highlight ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90" : "bg-card text-foreground border-border hover:bg-secondary hover:border-primary/60 hover:text-foreground"}`} aria-label={action.label}>
                            <Icon className={`size-4 ${action.highlight ? "text-primary-foreground" : "text-foreground/80"}`} />
                            <span>{action.label}</span>
                            {action.isExternal && <ExternalLink className="size-3 text-muted-foreground ml-0.5 opacity-60" />}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          {action.tooltip}
                        </TooltipContent>
                      </Tooltip>
                    </div>;
              })}
              </div>
            </div>

            {}
            <div className="mt-6 pt-5 border-t border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-muted-foreground font-default">
              <div className="flex items-center gap-2 overflow-hidden">
                <span className="font-semibold text-foreground shrink-0 uppercase tracking-wider text-[11px]">
                  {isZh ? "標準網址：" : "Canonical URL:"}
                </span>
                <span className="truncate font-mono text-[11px] bg-secondary px-2 py-0.5 border border-border text-foreground/90 max-w-xs sm:max-w-md md:max-w-lg select-all">
                  {currentUrl}
                </span>
              </div>
              <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                <span className="inline-block size-1.5 rounded-full bg-primary" />
                <span>{isZh ? "Elite Group OEM/ODM 官方入口" : "Elite Group OEM/ODM Official Portal"}</span>
              </div>
            </div>

            {}
            <div aria-live="polite" aria-atomic="true" className="sr-only">
              {statusMessage}
            </div>
          </motion.div>
        </TooltipProvider>
      </div>
    </section>;
}